import React, { useEffect, useState } from "react";
import { AnimatePresence, motion } from "framer-motion";

export default function ContactPopup({
  apiUrl,
  leadsIngestUrl,
  delayMs = 3000,
  privacyHref = "/privacy"
}) {
  const [open, setOpen] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [done, setDone] = useState(false);
  const [form, setForm] = useState({ name: "", email: "", company: "" });
  const [error, setError] = useState("");

  useEffect(() => {
    const hasPlayed = localStorage.getItem("hasPlayed") === "true";
    const dismissed = localStorage.getItem("contactPopupDismissed") === "true";
    if (!hasPlayed || dismissed) return;

    const timer = setTimeout(() => setOpen(true), delayMs);
    return () => clearTimeout(timer);
  }, [delayMs]);

  const close = () => {
    setOpen(false);
    localStorage.setItem("contactPopupDismissed", "true");
  };

  const onChange = (e) =>
    setForm((f) => ({ ...f, [e.target.name]: e.target.value }));

  const onSubmit = async (e) => {
    e.preventDefault();
    setError("");

    const name = form.name.trim();
    const email = form.email.trim();
    const company = form.company.trim();

    if (!name) return setError("Please enter your name.");
    if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email))
      return setError("Please enter a valid email.");

    try {
      setSubmitting(true);

      const endpoint = leadsIngestUrl || (apiUrl ? `${apiUrl}/api/contact-lead` : "");
      if (endpoint) {
        await fetch(endpoint, {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({
            name,
            email,
            company: company || null,
            source: "demo-site"
          })
        });
      }

      setDone(true);
      localStorage.setItem("contactPopupDismissed", "true");
      setTimeout(() => setOpen(false), 1600);
    } catch (err) {
      console.error("contact lead failed", err);
      setError("Something went wrong. Please try again.");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <AnimatePresence>
      {open && (
        <>
          <motion.div
            className="fixed inset-0 bg-black/50 z-50"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
          />
          <motion.div
            className="fixed inset-0 z-50 flex items-center justify-center px-4"
            initial={{ opacity: 0, y: 24 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: 24 }}
          >
            <div className="w-full max-w-md rounded-2xl bg-white shadow-2xl">
              <div className="p-6 sm:p-7 relative">
                <button
                  onClick={close}
                  aria-label="Close"
                  className="absolute right-4 top-4 text-gray-400 hover:text-gray-600"
                >
                  X
                </button>

                <h2 className="text-xl sm:text-2xl font-extrabold text-[#2b3b4b]">
                  Interested in learning more?
                </h2>
                <p className="text-sm text-gray-600 mt-1">
                  Glad you enjoyed the demo. Share your details and we will
                  follow up with more information.
                </p>

                {done ? (
                  <div className="mt-6 text-sm text-[#2b3b4b]">
                    Thanks, we received your details!
                  </div>
                ) : (
                  <form onSubmit={onSubmit} className="mt-5 space-y-4">
                    <div>
                      <label className="block text-xs font-semibold text-[#2b3b4b] mb-1">
                        Name
                      </label>
                      <input
                        name="name"
                        placeholder="Jane Doe"
                        className="w-full rounded-md border border-gray-300 px-3 py-2 text-sm outline-none focus:ring-2 focus:ring-[#00aca9]"
                        value={form.name}
                        onChange={onChange}
                      />
                    </div>

                    <div>
                      <label className="block text-xs font-semibold text-[#2b3b4b] mb-1">
                        Email
                      </label>
                      <input
                        name="email"
                        type="email"
                        placeholder="you@example.com"
                        className="w-full rounded-md border border-gray-300 px-3 py-2 text-sm outline-none focus:ring-2 focus:ring-[#00aca9]"
                        value={form.email}
                        onChange={onChange}
                      />
                    </div>

                    <div>
                      <label className="block text-xs font-semibold text-[#2b3b4b] mb-1">
                        Company (Optional)
                      </label>
                      <input
                        name="company"
                        placeholder="Your Company Inc."
                        className="w-full rounded-md border border-gray-300 px-3 py-2 text-sm outline-none focus:ring-2 focus:ring-[#00aca9]"
                        value={form.company}
                        onChange={onChange}
                      />
                    </div>

                    {error && (
                      <p className="text-xs text-red-600 -mt-1">{error}</p>
                    )}

                    <button
                      type="submit"
                      disabled={submitting}
                      className="w-full mt-2 rounded-md bg-[#5b68ff] hover:opacity-95 text-white py-3 text-sm font-semibold flex items-center justify-center gap-2 disabled:opacity-60"
                    >
                      <span>+</span> Connect With Us
                    </button>

                    <p className="text-[11px] text-gray-500 text-center">
                      We respect your privacy. By submitting, you agree to our{" "}
                      <a
                        className="underline"
                        href={privacyHref}
                        target="_blank"
                        rel="noreferrer"
                      >
                        Privacy Policy
                      </a>
                      .
                    </p>
                  </form>
                )}
              </div>
            </div>
          </motion.div>
        </>
      )}
    </AnimatePresence>
  );
}
